/**
 * Phase A1a — /packages
 *
 * Prepaid credit packages cockpit. Read-only Offerings sub-view for now;
 * Outstanding + Redemptions land in A3/A4b alongside the consumer Buy flow.
 *
 * Shows the calling clinic's service_package templates + tier ladders.
 * No write capability yet — A1b enables CRUD via OfferingEditorDialog.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  Archive,
  ArchiveRestore,
  ChevronLeft,
  Loader2,
  Package as PackageIcon,
  Pencil,
  Plus,
} from 'lucide-react'

import { billing, type ServicePackage } from '../api/client'
import { Button } from '../components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card'
import { OfferingEditorDialog } from './OfferingEditorDialog'
import { PackagesAddons } from './PackagesAddons'
import { PackagesOutstanding } from './PackagesOutstanding'
import { PackagesRedemptionsLog } from './PackagesRedemptionsLog'

function formatMoney(minor: number, currency: string, locale: string): string {
  // Major = minor / 100 (TWD is normally rendered without decimals).
  const major = minor / 100
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(major)
  } catch {
    return `${currency} ${major}`
  }
}

function tierBadge(t: ServicePackage['tiers'][number]): string {
  return t.bonus_sessions > 0
    ? `${t.paid_sessions}+${t.bonus_sessions}`
    : `${t.paid_sessions}`
}

export default function Packages() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const isZh = i18n.language?.toLowerCase().startsWith('zh')

  const [tab, setTab] = useState<
    'offerings' | 'addons' | 'outstanding' | 'redemptions'
  >('offerings')
  const [items, setItems] = useState<ServicePackage[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [includeArchived, setIncludeArchived] = useState(false)
  const [serviceType, setServiceType] = useState<string>('all')
  const [reloadTick, setReloadTick] = useState(0)
  const [editorOpen, setEditorOpen] = useState(false)
  const [editingPkg, setEditingPkg] = useState<ServicePackage | null>(null)
  const [archiveBusyId, setArchiveBusyId] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    setItems(null)
    setError(null)
    billing
      .listPackages({
        include_archived: includeArchived,
        service_type:
          serviceType === 'all'
            ? undefined
            : (serviceType as 'boarding' | 'daycare' | 'grooming'),
      })
      .then((r) => {
        if (alive) setItems(r.items)
      })
      .catch(() => {
        if (alive) setError('load_failed')
      })
    return () => {
      alive = false
    }
  }, [includeArchived, serviceType, reloadTick])

  function openCreate() {
    setEditingPkg(null)
    setEditorOpen(true)
  }
  function openEdit(p: ServicePackage) {
    setEditingPkg(p)
    setEditorOpen(true)
  }
  async function toggleArchive(p: ServicePackage) {
    setArchiveBusyId(p.id)
    try {
      if (p.archived_at) {
        await billing.restorePackage(p.id)
      } else {
        await billing.archivePackage(p.id)
      }
      setReloadTick((n) => n + 1)
    } catch (e) {
      setError((e as { detail?: string }).detail ?? 'archive_failed')
    } finally {
      setArchiveBusyId(null)
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            className="gap-1"
          >
            <ChevronLeft className="h-4 w-4" />
            {t('packages.back')}
          </Button>
          <h1 className="text-lg font-semibold">{t('packages.title')}</h1>
          <div className="w-20" />
        </div>
      </header>

      <main className="mx-auto max-w-5xl space-y-6 px-6 py-8">
        <nav className="flex gap-1 border-b" data-testid="packages-tabs">
          <button
            type="button"
            onClick={() => setTab('offerings')}
            className={`px-4 py-2 text-sm transition-colors ${
              tab === 'offerings'
                ? 'border-b-2 border-primary font-medium text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-offerings"
          >
            {t('packages.offerings.title')}
          </button>
          <button
            type="button"
            onClick={() => setTab('addons')}
            className={`px-4 py-2 text-sm transition-colors ${
              tab === 'addons'
                ? 'border-b-2 border-primary font-medium text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-addons"
          >
            {t('packages.addOns.title')}
          </button>
          <button
            type="button"
            onClick={() => setTab('outstanding')}
            className={`px-4 py-2 text-sm transition-colors ${
              tab === 'outstanding'
                ? 'border-b-2 border-primary font-medium text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-outstanding"
          >
            {t('packages.outstanding.title')}
          </button>
          <button
            type="button"
            onClick={() => setTab('redemptions')}
            className={`px-4 py-2 text-sm transition-colors ${
              tab === 'redemptions'
                ? 'border-b-2 border-primary font-medium text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-redemptions"
          >
            {t('packages.redemptions.title')}
          </button>
        </nav>

        {tab === 'addons' && <PackagesAddons />}
        {tab === 'outstanding' && <PackagesOutstanding />}
        {tab === 'redemptions' && <PackagesRedemptionsLog />}

        {tab === 'offerings' && (
        <>
        <div>
          <h2 className="text-2xl font-semibold">{t('packages.offerings.title')}</h2>
          <p className="text-sm text-muted-foreground">
            {t('packages.offerings.subtitle')}
          </p>
        </div>

        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center gap-3">
              <CardTitle className="flex-1">
                {t('packages.offerings.cardTitle')}
              </CardTitle>
              <select
                value={serviceType}
                onChange={(e) => setServiceType(e.target.value)}
                className="h-9 rounded border px-2 text-sm"
                data-testid="filter-service-type"
              >
                <option value="all">{t('packages.filter.allServices')}</option>
                <option value="boarding">{t('packages.serviceType.boarding')}</option>
                <option value="daycare">{t('packages.serviceType.daycare')}</option>
                <option value="grooming">{t('packages.serviceType.grooming')}</option>
              </select>
              <label className="flex items-center gap-1 text-sm">
                <input
                  type="checkbox"
                  checked={includeArchived}
                  onChange={(e) => setIncludeArchived(e.target.checked)}
                />
                {t('packages.filter.includeArchived')}
              </label>
              <Button
                size="sm"
                onClick={openCreate}
                data-testid="new-package"
                className="ml-auto"
              >
                <Plus className="mr-1 h-4 w-4" />
                {t('packages.editor.newPackage')}
              </Button>
            </div>
            {items && (
              <CardDescription data-testid="package-count">
                {t('packages.offerings.countSimple', { count: items.length })}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>
            {items === null && !error && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('packages.loading')}
              </div>
            )}
            {error && (
              <p className="text-sm text-destructive">
                {t('packages.errors.loadFailed')}
              </p>
            )}
            {items && items.length === 0 && (
              <div className="flex flex-col items-center gap-3 py-12 text-center">
                <PackageIcon className="h-10 w-10 text-muted-foreground/40" />
                <p className="font-medium">{t('packages.empty.title')}</p>
                <p className="text-sm text-muted-foreground">
                  {t('packages.empty.subtitle')}
                </p>
              </div>
            )}
            {items && items.length > 0 && (
              <div className="grid gap-4 sm:grid-cols-2">
                {items.map((p) => {
                  const name = isZh ? p.name_zh : p.name_en
                  const description = isZh ? p.description_zh : p.description_en
                  const defaultTier =
                    p.tiers.find((tier) => tier.is_default) ?? p.tiers[0]
                  return (
                    <div
                      key={p.id}
                      className={`rounded-lg border p-4 ${p.archived_at ? 'opacity-60' : ''}`}
                      data-testid="package-card"
                    >
                      <div className="mb-2 flex items-start justify-between gap-2">
                        <h3 className="font-medium leading-tight">{name}</h3>
                        <div className="flex items-center gap-1">
                          <span className="rounded bg-muted px-2 py-0.5 text-xs">
                            {t(`packages.serviceType.${p.service_type}`)}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() => openEdit(p)}
                            aria-label={t('packages.editor.editButton')}
                            data-testid={`edit-${p.id}`}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() => toggleArchive(p)}
                            disabled={archiveBusyId === p.id}
                            aria-label={
                              p.archived_at
                                ? t('packages.editor.restoreButton')
                                : t('packages.editor.archiveButton')
                            }
                            data-testid={`archive-${p.id}`}
                          >
                            {archiveBusyId === p.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : p.archived_at ? (
                              <ArchiveRestore className="h-3.5 w-3.5" />
                            ) : (
                              <Archive className="h-3.5 w-3.5" />
                            )}
                          </Button>
                        </div>
                      </div>
                      {description && (
                        <p className="mb-3 line-clamp-2 text-sm text-muted-foreground">
                          {description}
                        </p>
                      )}
                      <div className="mb-3 flex flex-wrap gap-1.5">
                        {p.tiers.map((tier) => (
                          <span
                            key={tier.id}
                            className={`rounded border px-2 py-0.5 text-xs ${tier.is_default ? 'border-primary bg-primary/10 text-primary' : 'border-muted-foreground/30 text-muted-foreground'}`}
                          >
                            {tierBadge(tier)}
                          </span>
                        ))}
                      </div>
                      <div className="space-y-1 text-sm">
                        {defaultTier && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">
                              {t('packages.card.defaultPrice')}
                            </span>
                            <span className="font-medium">
                              {formatMoney(
                                defaultTier.price_minor,
                                p.currency,
                                i18n.language,
                              )}
                            </span>
                          </div>
                        )}
                        {p.validity_days && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">
                              {t('packages.card.validity')}
                            </span>
                            <span>
                              {t('packages.card.validityValue', {
                                days: p.validity_days,
                              })}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            {t('packages.card.refund')}
                          </span>
                          <span>
                            {t(`packages.refundPolicy.${p.refund_policy_mode}`)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            {t('packages.card.transferability')}
                          </span>
                          <span>
                            {t(`packages.transferability.${p.transferability}`)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </CardContent>
        </Card>
        </>
        )}

      </main>

      <OfferingEditorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        existing={editingPkg}
        onSaved={() => setReloadTick((n) => n + 1)}
      />
    </div>
  )
}
